'use client';

import { CardMedia } from '@sbercloud/uikit-product-site-cards';
import { LayoutType } from '@sbercloud/uikit-product-utils';
import { ChipToggle } from '@snack-uikit/chips';
import { Pagination } from '@snack-uikit/pagination';
import { Typography } from '@snack-uikit/typography';
import cn from 'classnames';
import { useParams, useRouter } from 'next/navigation';
import { Fragment, MouseEvent } from 'react';
import { Link } from 'src/components/Link';
import { SubscribeBanner } from 'src/components/SubscribeBanner';
import { SubscribeSocialBanner } from 'src/components/SubscribeSocialBanner';
import { Wrapper } from 'src/components/Wrapper';
import { useAnalytics } from 'src/hooks/useAnalytics';
import { useLayoutType } from 'src/hooks/useLayoutType';
import { useWindowWidth } from 'src/hooks/useWindowWidth';
import { Layout } from 'src/layout';
import { ClickAnalyticsType } from 'src/types';
import { BlogsProps } from 'src/types/backendContent';
import { Breadcrumbs } from 'src/uikit/Breadcrumbs';
import { NewsCarousel } from 'src/uikit/Carousels/NewsCarousel';
import { formatDate } from 'src/utils/formatDate';

import { getTitleTypographyProps } from '../contacts/utils';
import s from './Blog.module.scss';

export type BlogDistributionPageProps = {
  blogsData: BlogsProps[];
  categories: BlogsProps['categories'];
  newsData: BlogsProps[];
  maxPages: number;
  currentPage: number;
  activeCategory?: string | null;
  categoryTitle?: string;
};

function NewsItem({ news }: { news: BlogsProps }) {
  const { getAdaptiveSizeTypography } = useWindowWidth();

  return (
    <Link
      className={s.news}
      href={`/blog/${news.slug}`}
      id={`blog-news-card-link-${news.title}`}
      title={news.title}
      analyticsOptions={{
        action: `/blog/${news.slug}`,
        clickZone: 'body',
        clickElement: 'cardclick',
        clickContent: news.title,
        uniqueId: `blog-card-link-${news.title}`,
        transitionType: 'inside-link',
        clickZoneCategory: 'site-body',
        transitionUrl: `/blog/${news.slug}`,
        transitionTypeMetrics: 'internal',
      }}
    >
      <div className={s.newsDate}>
        <Typography.SansBodyM tag="div">
          {formatDate(news.releaseDate)}
        </Typography.SansBodyM>
      </div>
      <div className={s.newsTitle}>
        <Typography
          tag="div"
          family="sans"
          {...getAdaptiveSizeTypography({
            desktop: { purpose: 'title', size: 'm' },
            tablet: { purpose: 'title', size: 's' },
          })}
        >
          <span>{news.title}</span>
        </Typography>
      </div>
    </Link>
  );
}

function NewsList({
  newsData,
  maxCount = newsData.length,
  className,
}: {
  newsData: BlogsProps[];
  maxCount?: number;
  className?: string;
}) {
  const layoutType = useLayoutType();

  return (
    <div className={cn(s.newsBlock, className)}>
      <Typography
        tag="h3"
        family="sans"
        {...getTitleTypographyProps(layoutType)}
      >
        Новости
      </Typography>

      <div className={s.newsList}>
        {newsData.slice(0, maxCount).map((news) => {
          return <NewsItem key={news.releaseDate} news={news} />;
        })}
      </div>
    </div>
  );
}

function splitArrayToChunks(array: BlogsProps[]) {
  const splitArray = [];
  const chunkSize = 4;

  for (
    let i = 0, k = 0;
    i < array.length;
    k === 1 ? (i += chunkSize + 1) : (i += chunkSize), k += 1
  ) {
    if (k === 1) {
      splitArray.push(array.slice(i, i + (chunkSize + 1)));
    } else {
      splitArray.push(array.slice(i, i + chunkSize));
    }
  }

  return splitArray;
}

function renderFirstSection(
  array: BlogsProps[],
  newsData: BlogsProps[],
  layoutType: LayoutType,
  clickAnalytics: (variables: ClickAnalyticsType) => void,
) {
  if (array.length === 4) {
    return (
      <>
        {array.map((blog, index) => {
          if (index === array.length - 1) {
            return (
              <Fragment key={`${blog.id}_${index}`}>
                <div className={s.col}>
                  <CardMedia
                    size="l"
                    title={blog.title}
                    date={formatDate(blog.releaseDate)}
                    img={blog.cover}
                    href={`/blog/${blog.slug}`}
                    titleTag="span"
                    time={blog.timeToRead}
                    tag={{
                      label: blog.categoryTitle,
                    }}
                    layoutType={layoutType}
                    onClick={() => {
                      clickAnalytics({
                        action: `/blog/${blog.slug}`,
                        clickZone: 'body',
                        clickElement: 'cardclick',
                        clickContent: blog.title,
                        uniqueId: `blog-card-${blog.title}`,
                        transitionType: 'inside-link',
                        clickZoneCategory: 'site-body',
                        transitionUrl: `/blog/${blog.slug}`,
                        transitionTypeMetrics: 'internal',
                      });
                    }}
                  />
                </div>
                <div className={s.col}>
                  <SubscribeSocialBanner clickAnalytics={clickAnalytics} />
                </div>
              </Fragment>
            );
          }
          return (
            <div
              key={`${blog.id}_${index}`}
              className={cn(s.col, {
                [s.largeCard]: index === 0,
              })}
            >
              <CardMedia
                size={index === 0 ? 'l' : 'm'}
                title={blog.title}
                date={formatDate(blog.releaseDate)}
                img={blog.cover}
                href={`/blog/${blog.slug}`}
                titleTag="span"
                time={blog.timeToRead}
                tag={{
                  label: blog.categoryTitle,
                }}
                layoutType={layoutType}
                onClick={() => {
                  clickAnalytics({
                    action: `/blog/${blog.slug}`,
                    clickZone: 'body',
                    clickElement: 'cardclick',
                    clickContent: blog.title,
                    uniqueId: `blog-card-${blog.title}`,
                    transitionType: 'inside-link',
                    clickZoneCategory: 'site-body',
                    transitionUrl: `/blog/${blog.slug}`,
                    transitionTypeMetrics: 'internal',
                  });
                }}
              />
            </div>
          );
        })}
        {newsData && newsData.length > 0 && (
          <div className={cn(s.col, s.largeCard, s.hiddenDesktop)}>
            <NewsList
              newsData={newsData}
              maxCount={3}
              className={s.mobileNewsList}
            />
          </div>
        )}
      </>
    );
  } else {
    return array.map((blog, index) => {
      return (
        <div
          key={index}
          className={cn(s.col, {
            [s.largeCard]: index === 0,
          })}
        >
          <CardMedia
            size={index === 0 ? 'l' : 'm'}
            title={blog.title}
            date={formatDate(blog.releaseDate)}
            img={blog.cover}
            href={`/blog/${blog.slug}`}
            titleTag="span"
            time={blog.timeToRead}
            tag={{
              label: blog.categoryTitle,
            }}
            layoutType={layoutType}
            onClick={() => {
              clickAnalytics({
                action: `/blog/${blog.slug}`,
                clickZone: 'body',
                clickElement: 'cardclick',
                clickContent: blog.title,
                uniqueId: `blog-card-${blog.title}`,
                transitionType: 'inside-link',
                clickZoneCategory: 'site-body',
                transitionUrl: `/blog/${blog.slug}`,
                transitionTypeMetrics: 'internal',
              });
            }}
          />
        </div>
      );
    });
  }
}

function renderSecondSection(
  array: BlogsProps[],
  layoutType: LayoutType,
  clickAnalytics: (variables: ClickAnalyticsType) => void,
) {
  if (array.length === 5) {
    return array.map((blog, index) => {
      if (index === array.length - 1) {
        return (
          <Fragment key={`${blog.id}_${index}`}>
            <div className={s.col}>
              <CardMedia
                size="m"
                title={blog.title}
                date={formatDate(blog.releaseDate)}
                img={blog.cover}
                href={`/blog/${blog.slug}`}
                titleTag="span"
                time={blog.timeToRead}
                tag={{
                  label: blog.categoryTitle,
                }}
                layoutType={layoutType}
                onClick={() => {
                  clickAnalytics({
                    action: `/blog/${blog.slug}`,
                    clickZone: 'body',
                    clickElement: 'cardclick',
                    clickContent: blog.title,
                    uniqueId: `blog-card-${blog.title}`,
                    transitionType: 'inside-link',
                    clickZoneCategory: 'site-body',
                    transitionUrl: `/blog/${blog.slug}`,
                    transitionTypeMetrics: 'internal',
                  });
                }}
              />
            </div>
            <div className={cn(s.col, s.largeCard)}>
              <SubscribeBanner clickAnalytics={clickAnalytics} />
            </div>
          </Fragment>
        );
      }
      return (
        <div
          key={`${blog.id}_${index}`}
          className={cn(s.col, {
            [s.largeCard]: index === 0,
          })}
        >
          <CardMedia
            size={index === 0 ? 'l' : 'm'}
            title={blog.title}
            date={formatDate(blog.releaseDate)}
            img={blog.cover}
            href={`/blog/${blog.slug}`}
            titleTag="span"
            time={blog.timeToRead}
            tag={{
              label: blog.categoryTitle,
            }}
            layoutType={layoutType}
            onClick={() => {
              clickAnalytics({
                action: `/blog/${blog.slug}`,
                clickZone: 'body',
                clickElement: 'cardclick',
                clickContent: blog.title,
                uniqueId: `blog-card-${blog.title}`,
                transitionType: 'inside-link',
                clickZoneCategory: 'site-body',
                transitionUrl: `/blog/${blog.slug}`,
                transitionTypeMetrics: 'internal',
              });
            }}
          />
        </div>
      );
    });
  } else {
    return array.map((blog, index) => {
      return (
        <div
          key={index}
          className={cn(s.col, {
            [s.largeCard]: index === 0,
          })}
        >
          <CardMedia
            size={index === 0 ? 'l' : 'm'}
            title={blog.title}
            date={formatDate(blog.releaseDate)}
            img={blog.cover}
            href={`/blog/${blog.slug}`}
            titleTag="span"
            time={blog.timeToRead}
            tag={{
              label: blog.categoryTitle,
            }}
            layoutType={layoutType}
            onClick={() => {
              clickAnalytics({
                action: `/blog/${blog.slug}`,
                clickZone: 'body',
                clickElement: 'cardclick',
                clickContent: blog.title,
                uniqueId: `blog-card-${blog.title}`,
                transitionType: 'inside-link',
                clickZoneCategory: 'site-body',
                transitionUrl: `/blog/${blog.slug}`,
                transitionTypeMetrics: 'internal',
              });
            }}
          />
        </div>
      );
    });
  }
}

function renderThirdSection(
  array: BlogsProps[],
  newsData: BlogsProps[],
  layoutType: LayoutType,
  clickAnalytics: (variables: ClickAnalyticsType) => void,
) {
  if (array.length > 0) {
    return array.map((blog, index) => {
      if (index === 0) {
        return (
          <Fragment key={`${blog.id}_${index}`}>
            {newsData && newsData.length > 0 && (
              <div className={cn(s.col, s.largeCard)}>
                <NewsCarousel
                  blogs={newsData}
                  title="Читают чаще всего"
                  className={s.carousel}
                />
              </div>
            )}
            <div key={index} className={s.col}>
              <CardMedia
                size="m"
                title={blog.title}
                date={formatDate(blog.releaseDate)}
                img={blog.cover}
                href={`/blog/${blog.slug}`}
                titleTag="span"
                time={blog.timeToRead}
                tag={{
                  label: blog.categoryTitle,
                }}
                layoutType={layoutType}
                onClick={() => {
                  clickAnalytics({
                    action: `/blog/${blog.slug}`,
                    clickZone: 'body',
                    clickElement: 'cardclick',
                    clickContent: blog.title,
                    uniqueId: `blog-card-${blog.title}`,
                    transitionType: 'inside-link',
                    clickZoneCategory: 'site-body',
                    transitionUrl: `/blog/${blog.slug}`,
                    transitionTypeMetrics: 'internal',
                  });
                }}
              />
            </div>
          </Fragment>
        );
      }
      return (
        <div
          key={`${blog.id}_${index}`}
          className={cn(s.col, {
            [s.largeCard]: index === 0,
          })}
        >
          <CardMedia
            size="m"
            title={blog.title}
            date={formatDate(blog.releaseDate)}
            img={blog.cover}
            href={`/blog/${blog.slug}`}
            titleTag="span"
            time={blog.timeToRead}
            tag={{
              label: blog.categoryTitle,
            }}
            layoutType={layoutType}
            onClick={() => {
              clickAnalytics({
                action: `/blog/${blog.slug}`,
                clickZone: 'body',
                clickElement: 'cardclick',
                clickContent: blog.title,
                uniqueId: `blog-card-${blog.title}`,
                transitionType: 'inside-link',
                clickZoneCategory: 'site-body',
                transitionUrl: `/blog/${blog.slug}`,
                transitionTypeMetrics: 'internal',
              });
            }}
          />
        </div>
      );
    });
  }
}

function BlogCards({
  blogs,
  news,
}: {
  blogs: BlogsProps[] | undefined;
  news: BlogsProps[];
}) {
  const layoutType = useLayoutType();
  const { clickAnalytics } = useAnalytics();

  if (blogs) {
    const data = splitArrayToChunks(blogs);

    return data.map((blogArray, mainIndex) => {
      switch (mainIndex) {
        case 0:
          return renderFirstSection(
            blogArray,
            news,
            layoutType,
            clickAnalytics,
          );
        case 1:
          return renderSecondSection(blogArray, layoutType, clickAnalytics);
        case 2:
          return renderThirdSection(
            blogArray,
            news,
            layoutType,
            clickAnalytics,
          );
        default:
          return null;
      }
    });
  }

  return null;
}

function Chips({
  categories,
  activeCategory,
}: {
  categories: BlogDistributionPageProps['categories'];
  activeCategory?: string | null;
}) {
  const {
    windowWidth,
    breakpoints: { MD },
  } = useWindowWidth();

  const { clickAnalytics } = useAnalytics();

  const { push } = useRouter();

  const defaultChips = {
    title: 'Все',
    checked: activeCategory ? false : true,
    slug: '/blog',
  };

  const chips = categories.map((chip) => {
    return (
      <ChipToggle
        key={chip.id}
        label={chip.title}
        size={windowWidth && windowWidth > MD ? 'm' : 's'}
        checked={chip.slug === activeCategory ? true : false}
        onChange={() => {
          clickAnalytics({
            action: `/blog/category/${chip.slug}`,
            clickZone: 'body',
            clickElement: 'button',
            clickContent: chip.title,
            uniqueId: `blog-category-chip-button-${chip.slug}`,
            transitionType: 'inside-link',
            clickZoneCategory: 'site-body',
            transitionUrl: `/blog/category/${chip.slug}`,
            transitionTypeMetrics: 'internal',
          });
          push(`/blog/category/${chip.slug}`);
        }}
      />
    );
  });

  return (
    <div className={s.chipsGrid}>
      <>
        <ChipToggle
          label={defaultChips.title}
          size={windowWidth && windowWidth > MD ? 'm' : 's'}
          checked={defaultChips.checked}
          onChange={() => {
            clickAnalytics({
              action: `/blog`,
              clickZone: 'body',
              clickElement: 'button',
              clickContent: defaultChips.title,
              uniqueId: `blog-category-chip-button-${defaultChips.title}`,
              transitionType: 'inside-link',
              clickZoneCategory: 'site-body',
              transitionUrl: `/blog`,
              transitionTypeMetrics: 'internal',
            });

            push('/blog');
          }}
        />
        {chips}
      </>
    </div>
  );
}

const DEFAULT_SUBTITLE =
  'Узнайте о самых актуальных трендах и новостях в облачных технологиях из блога Cloud.ru';

export function BlogPage({
  blogsData = [],
  newsData = [],
  maxPages = 1,
  currentPage = 1,
  categories,
  activeCategory,
  categoryTitle,
}: BlogDistributionPageProps) {
  const params = useParams();
  const router = useRouter();
  const {
    windowWidth,
    breakpoints: { MD },
  } = useWindowWidth();
  const layoutType = useLayoutType();
  const { clickAnalytics } = useAnalytics();

  const handleChangePage = (
    page: number,
    event?: MouseEvent<HTMLAnchorElement>,
  ) => {
    event?.preventDefault();
    if (page === currentPage) return;
    let rout = '';

    if (activeCategory) {
      rout =
        page === 1
          ? `/blog/category/${activeCategory}`
          : `/blog/category/${activeCategory}/page/${page}`;
    } else {
      rout = page === 1 ? `/blog` : `/blog/page/${page}`;
    }

    clickAnalytics({
      action: rout,
      clickZone: 'body',
      clickElement: 'button',
      clickContent: page.toString(),
      uniqueId: `blog-pagination-button-${page}`,
      transitionType: 'inside-link',
      clickZoneCategory: 'site-body',
      transitionUrl: rout,
      transitionTypeMetrics: 'internal',
    });

    const blogParam = params?.slug;

    if (!blogParam && page === 1) {
      window.location.pathname = activeCategory
        ? `/blog/category/${activeCategory}`
        : '/blog';
    }

    if (blogParam === page.toString()) {
      window.location.pathname = activeCategory
        ? `/blog/category/${activeCategory}/page/${blogParam}`
        : `/blog/page/${blogParam}`;
    }

    router.push(rout);
  };

  const BLOG_MOCK = {
    seo: {
      metaTitle: `Блог про облачные технологии в России | Облако Cloud.ru`,
      metaDescription: `Блог про облачные технологии в России. Надежность, качество и безопасность! Тел: +7 495 260-10-82`,
    },
    all: {
      id: '0',
      title: 'Все',
      slug: 'all',
    },
    breadcrumbs: [
      {
        title: 'Главная',
        link: '/',
      },
      { title: 'Блог', link: '' },
    ],
    buttonTitle: 'Показать еще',
  };

  const { seo, breadcrumbs } = BLOG_MOCK;

  if (currentPage !== 1) {
    seo.metaTitle = `${seo.metaTitle} | Страница ${currentPage}`;
    seo.metaDescription = `${seo.metaDescription} | Страница ${currentPage}`;
  }

  const hrefFormatter = (page: number) => {
    if (activeCategory) {
      return page === 1
        ? `/blog/category/${activeCategory}`
        : `/blog/category/${activeCategory}/page/${page}`;
    } else {
      return page === 1 ? `/blog` : `/blog/page/${page}`;
    }
  };

  return (
    <Layout>
      <div className={s.root}>
        <Wrapper>
          <div className={s.header}>
            <Breadcrumbs links={breadcrumbs} className={s.breadcrumbs} />
            <h1 className={s.headerTitle} data-layout-type={layoutType}>
              Блог
            </h1>
            <Typography.SansBodyL className={s.headerSubtitle} tag="p">
              {categoryTitle ? `Рубрика: ${categoryTitle}` : DEFAULT_SUBTITLE}
            </Typography.SansBodyL>
          </div>
        </Wrapper>
        <Wrapper>
          <div className={s.pageBlogHeader}>
            <div className={s.grid}>
              <div>
                <Chips
                  categories={categories}
                  activeCategory={activeCategory}
                />
              </div>
              {/* <div>
                <Search
                  placeholder="Поиск по материалам"
                  size="m"
                  value=""
                  onChange={() => null}
                  className={cn(s.search, s.border)}
                />
              </div> */}
            </div>
          </div>
          <div className={s.grid}>
            <div>
              {blogsData && (
                <section className={s.container}>
                  <div className={s.cards}>
                    {blogsData.length > 0 && (
                      <BlogCards blogs={blogsData} news={newsData} />
                    )}
                  </div>

                  {maxPages > 1 && (
                    <Pagination
                      page={currentPage}
                      total={maxPages}
                      onChange={handleChangePage}
                      hrefFormatter={hrefFormatter}
                      variant="link"
                    />
                  )}
                </section>
              )}
            </div>
            <div>
              {windowWidth &&
                windowWidth > MD &&
                newsData &&
                newsData.length > 0 && <NewsList newsData={newsData} />}
            </div>
          </div>
        </Wrapper>
      </div>
    </Layout>
  );
}
